/** The controlled values shared by the Chrome Metadata card and SERP preview. */
export interface MetadataValues {
  metaTitle: string;
  metaDescription: string;
  /** Post excerpt override. Empty = fall back to the automatic/derived excerpt. */
  excerpt: string;
  slug: string;
  ogTitle: string;
  ogDescription: string;
  /** Open Graph image URL override. Empty = fall back to the featured image. */
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  canonicalUrl: string;
  robotsOverride: '' | 'index' | 'noindex';
  wordpressAuthorId: number | null;
  schemaTypes: string[];
  keyword?: string;
}

/**
 * Meta title must reflect ONLY the stored SEO override — NEVER the H1
 * (`content.title`). A prior version of the rail's `contentMetadata`
 * derivation fell back to the H1 when `meta_title` was empty
 * (`content?.meta_title || content?.title || ''`), which meant the editor
 * silently held the H1 as if it were the real meta title and any autosave
 * PATCHed it verbatim into `meta_title` — permanently baking "meta title ==
 * H1" into the DB. Empty stays empty here; the SERP preview card's own
 * placeholder ("Untitled") covers the display-level fallback, and the
 * publisher's `meta_title or title` fallback (services/publisher) still
 * applies at publish time whenever this stays empty.
 */
export function deriveMetaTitle(rawMetaTitle: string | null | undefined): string {
  return rawMetaTitle || '';
}

/**
 * Translate controlled UI values into the existing blog-content PATCH shape.
 * Empty optional overrides deliberately become null so publishing retains its
 * automatic SEO/social/author fallback behavior.
 */
export function metadataPatchFields(values: MetadataValues) {
  return {
    // Empty meta title → null so the backend never persists an empty
    // override; publishing's own `meta_title or title` fallback (H1) keeps
    // applying until the user sets a real SEO title.
    meta_title: values.metaTitle || null,
    meta_description: values.metaDescription,
    // Empty excerpt → null so the backend restores the derived excerpt.
    excerpt: values.excerpt || null,
    url_slug: values.slug,
    og_title: values.ogTitle || null,
    og_description: values.ogDescription || null,
    og_image_url: values.ogImage || null,
    twitter_title: values.twitterTitle || null,
    twitter_description: values.twitterDescription || null,
    canonical_url: values.canonicalUrl || null,
    robots_override: values.robotsOverride || null,
    wordpress_author_id: values.wordpressAuthorId,
    schema_types_injected: values.schemaTypes,
    keyword: values.keyword || null,
  };
}
